import { useState, useEffect } from 'react';
import type { TommyPose } from '../components/TommyMascot';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import { ff, mono } from '../styles/fonts';
import PublicFooter from '../components/PublicFooter';
import TommyMascot from '../components/TommyMascot';
import { PricingContent } from './PricingPage';
import { useSchema } from '../hooks/useSchema';
import { usePageMeta } from '../hooks/usePageMeta';

const steps = [
  {
    num: '1',
    bgClass: 'bg-primary-container/20 text-primary-container',
    title: 'Upload',
    body: "Drop in your PDFs, slides, or raw notes. We handle the mess.",
  },
  {
    num: '2',
    bgClass: 'bg-[#b6e5d8] text-[#286958]',
    title: 'Personalized Path',
    body: "AI analyzes your content to build a custom learning journey.",
  },
  {
    num: '3',
    bgClass: 'bg-[#ff795c] text-[#ae3115]',
    title: 'Study',
    body: "Master the material with generated quizzes, flashcards, and videos.",
  },
];

const features = [
  {
    icon: 'psychology_alt',
    iconBg: 'bg-primary-container',
    iconColor: 'text-white',
    title: 'Adaptive Quizzes',
    body: "Questions that evolve as you learn. We identify your weak spots and focus on them until you're confident.",
  },
  {
    icon: 'style',
    iconBg: 'bg-[#286958]',
    iconColor: 'text-white',
    title: 'Smart Flashcards',
    body: "Spaced repetition built-in to make knowledge stick.",
  },
  {
    icon: 'menu_book',
    iconBg: 'bg-[#286958]',
    iconColor: 'text-white',
    title: 'Full Notes',
    body: "Your original material, enhanced and summarized.",
  },
  {
    icon: 'play_circle',
    iconBg: 'bg-[#241F1A]',
    iconColor: 'text-white',
    title: 'AI Video Lessons',
    body: "Tired of reading? Watch bite-sized video explainers generated specifically for your uploaded material.",
  },
];

const landingFaq = [
  {
    q: 'What is Learnex?',
    a: 'Learnex is an AI study platform that turns your own uploaded notes, PDFs, and lecture slides into flashcards, quizzes, and AI-narrated video lessons — all generated from and traceable back to your material.',
  },
  {
    q: 'Is Learnex free?',
    a: 'Yes. The free plan includes basic quizzes, flashcards, and audio lessons on one subject. Paid plans (Explorer at $4.99/mo and Master at $7.99/mo) unlock unlimited subjects and full AI video lessons. Students with a .edu email get 20% off paid plans.',
  },
  {
    q: 'How is Learnex different from Quizlet?',
    a: 'Quizlet is built around manually-made and community flashcard sets. Learnex generates flashcards, quizzes, and video lessons automatically from your own uploaded material, so everything maps directly to what you\'re actually studying.',
  },
  {
    q: 'How is Learnex different from Anki?',
    a: 'Anki is a manual spaced-repetition flashcard tool — you create the cards yourself. Learnex automates the entire process: upload your PDF or notes and it generates flashcards, quizzes, explanations, and video lessons from them automatically.',
  },
  {
    q: 'What file types does Learnex support?',
    a: 'You can upload PDFs, images of lecture slides, and scanned handwritten notes. Learnex uses AI vision to extract and understand the material regardless of format.',
  },
  {
    q: 'Does Learnex use my notes to train AI models?',
    a: 'No. Your uploaded material is processed only to generate your own study content and is never used to train AI models or shared with other users.',
  },
  {
    q: 'How long does it take to generate study materials?',
    a: 'Most uploads are processed within a few minutes. The AI generates topic explanations, quizzes, flashcards, and audio or video lessons automatically — you can start studying shortly after uploading.',
  },
];

function AccordionItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-outline-variant/15 last:border-b-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 py-5 text-left"
        aria-expanded={open}
      >
        <span className="text-body-lg text-on-surface font-medium">{q}</span>
        <span className="material-symbols-outlined text-ink-muted shrink-0" aria-hidden="true">
          {open ? 'remove' : 'add'}
        </span>
      </button>
      <div className={`text-body-md text-on-surface-variant leading-relaxed pb-5 pr-8 ${open ? 'block' : 'hidden'}`}>
        {a}
      </div>
    </div>
  );
}

function HeroTommyCycle() {
  const poses: { pose: TommyPose; animation: 'bounce' | 'float' | 'bob' | 'celebrate' | 'glow' }[] = [
    { pose: 'excited', animation: 'bounce' },
    { pose: 'happy', animation: 'bob' },
    { pose: 'idea', animation: 'glow' },
    { pose: 'cheerful', animation: 'bounce' },
    { pose: 'studying', animation: 'float' },
    { pose: 'teaching', animation: 'float' },
    { pose: 'proud', animation: 'celebrate' },
  ];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % poses.length);
    }, 2400);
    return () => clearInterval(timer);
  }, [poses.length]);

  const current = poses[index];

  return (
    <div className="flex flex-col items-center justify-center cursor-pointer select-none">
      <TommyMascot
        key={current.pose}
        pose={current.pose}
        size={230}
        animation={current.animation}
        interactive
        soundEnabled
        withShadow
      />
    </div>
  );
}

export default function LandingPage() {
  usePageMeta(
    'Learnex — AI Study Tool for Flashcards & Quizzes',
    'Upload lecture slides or PDFs to automatically generate explanations, adaptive quizzes, flashcards, and AI video lessons. Learnex helps you study smarter.'
  );

  useSchema({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': 'https://learnex.cc/#website',
        url: 'https://learnex.cc/',
        name: 'Learnex',
        description: 'AI study platform that turns uploaded course material into personalized explanations, quizzes, flashcards, and video lessons.',
        publisher: { '@id': 'https://learnex.cc/#organization' },
      },
      {
        '@type': 'Organization',
        '@id': 'https://learnex.cc/#organization',
        name: 'Learnex',
        alternateName: 'Learnex AI',
        url: 'https://learnex.cc/',
        logo: {
          '@type': 'ImageObject',
          url: 'https://learnex.cc/logo/logo-icon-tomato.svg',
          width: 512,
          height: 512,
        },
        description: 'Learnex (learnex.cc) is an AI-powered study platform for university and college students — not affiliated with Learnexus (learnexus.com) or LearnEx Group. Students upload their own course material (PDFs, slides, notes) and Learnex generates personalized flashcards, quizzes, and AI video lessons from it.',
        foundingDate: '2026',
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'support@learnex.cc',
          contactType: 'customer support',
        },
        sameAs: [
          'https://www.instagram.com/learnex.cc/',
          'https://www.linkedin.com/company/learnexcc',
          'https://www.youtube.com/@learnex_cc',
          'https://www.tiktok.com/@learnex.cc',
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: landingFaq.map(item => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      }
    ]
  });
  const [videoOpen, setVideoOpen] = useState(false);
  return (
    <div className="bg-paper-bg text-on-surface antialiased min-h-screen flex flex-col noise-bg">
      <PublicNav activePath="/" />

      {/* ── Hero ── */}
      <header className="relative pt-16 sm:pt-24 lg:pt-32 pb-20 lg:pb-32 overflow-hidden bg-gradient-to-br from-[#FFF5F2] to-paper-bg">
        <div className="max-w-[1200px] mx-auto px-base md:px-gutter grid lg:grid-cols-2 gap-12 items-center">
          
          {/* Left Column */}
          <div className="space-y-6 sm:space-y-8 max-w-xl">
            <div className="inline-flex items-center gap-2 bg-[#FF6B4A]/10 text-primary-container px-3 py-1.5 rounded-full text-sm font-medium">
              <span className="material-symbols-outlined text-sm" aria-hidden="true">auto_awesome</span>
              Smarter studying is here
            </div>
            <h1 className="text-4xl md:text-5xl text-on-surface font-semibold leading-tight max-w-lg" style={ff}>
              Turn your notes into flashcards, quizzes, and AI video lessons — <span className="text-primary-container underline decoration-primary-container underline-offset-8">your own personalized trail.</span>
            </h1>
            <p className="text-lg text-on-surface-variant">
              Upload your notes and let Learnex build a path of explanations, quizzes, and videos tailored just for you.
            </p>
            <div className="pt-4 flex flex-wrap items-center gap-4">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 bg-primary-container text-white px-8 py-4 rounded-full hover:-translate-y-0.5 hover:shadow-lg transition-all font-medium"
              >
                Get Started Free
              </Link>
              <button
                onClick={() => setVideoOpen(true)}
                className="inline-flex items-center gap-2 bg-transparent text-primary-container px-8 py-4 rounded-full hover:bg-primary/5 transition-all font-medium"
              >
                <span className="material-symbols-outlined" aria-hidden="true">play_circle</span>
                See how it works
              </button>
            </div>
          </div>

                      <div className="relative h-[420px] w-full flex items-center justify-center">
            <div className="absolute right-0 w-[420px] h-[420px] bg-gradient-to-tr from-white to-[#FFF5F2] rounded-full shadow-xl flex flex-col items-center justify-center border border-white/50 relative overflow-visible">
              <HeroTommyCycle />
            </div>

            {/* Floating Badge 1: Quiz Ready */}
            <div className="absolute -top-3 right-6 sm:right-14 bg-white shadow-lg rounded-full px-4 py-2 flex items-center gap-2 z-10 font-medium text-sm text-on-surface border border-outline-variant/15">
              <div className="w-8 h-8 rounded-full bg-[#286958] flex items-center justify-center text-white">
                <span className="material-symbols-outlined text-sm" aria-hidden="true">quiz</span>
              </div>
              Quiz Ready!
            </div>

            {/* Floating Badge 2: Active Recall */}
            <div className="absolute bottom-4 left-4 sm:left-12 bg-white shadow-lg rounded-full px-4 py-2 flex items-center gap-2 z-10 font-medium text-sm text-on-surface border border-outline-variant/15 animate-fade-in">
              <div className="w-8 h-8 rounded-full bg-[#FF6B4A] flex items-center justify-center text-white">
                <span className="material-symbols-outlined text-sm" aria-hidden="true">bolt</span>
              </div>
              Active Recall ⚡
            </div>
          </div>
        </div>
      </header>

      {/* ── Trust Proofs ── */}
      <div className="bg-paper-bg border-b border-outline-variant/10 py-12">
        <div className="max-w-[1200px] mx-auto px-base md:px-gutter flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
          <div className="flex flex-col items-center">
            <span className="text-3xl font-bold text-on-surface" style={ff}>5,000+</span>
            <span className="text-sm font-medium text-on-surface-variant uppercase tracking-wider">Notes Converted</span>
          </div>
          <div className="hidden md:block w-px h-12 bg-outline-variant/30"></div>
          <div className="flex flex-col items-center">
             <span className="text-3xl font-bold text-on-surface" style={ff}>4.6/5</span>
             <span className="text-sm font-medium text-on-surface-variant uppercase tracking-wider">Student Rating</span>
          </div>
          <div className="hidden md:block w-px h-12 bg-outline-variant/30"></div>
          <div className="flex flex-col items-center opacity-90 hover:opacity-100 transition-opacity">
             <a href="https://www.producthunt.com/products/learnex-turn-your-notes-into-study-path?utm_source=other&utm_medium=social" target="_blank" rel="noopener noreferrer">
               <img src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=123456&theme=light" alt="Learnex - Featured on Product Hunt" className="h-[48px]" />
             </a>
          </div>
        </div>
      </div>

      {/* ── Steps ── */}
      <section className="py-24 bg-[#FBF7F1] border-y border-outline-variant/10">
        <div className="max-w-[1200px] mx-auto px-base md:px-gutter">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl text-on-surface font-semibold" style={ff}>
              How to convert PDF notes into flashcards and quizzes?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Desktop connector line */}
            <div
              aria-hidden="true"
              className="hidden md:block absolute top-[52px] left-10 right-10 h-0.5 bg-outline-variant/30 z-0"
            />
            {steps.map(step => (
              <div
                key={step.title}
                className="bg-surface-white rounded-3xl p-10 shadow-[0_4px_24px_rgba(36,31,26,0.04)] text-center flex flex-col items-center z-10"
              >
                <div className={`w-14 h-14 ${step.bgClass} rounded-full flex items-center justify-center mb-6 text-xl font-medium shadow-sm`}>
                  {step.num}
                </div>
                <h3 className="text-xl font-semibold mb-3 text-on-surface" style={ff}>{step.title}</h3>
                <p className="text-on-surface-variant text-sm leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="py-24 bg-paper-bg">
        <div className="max-w-[1200px] mx-auto px-base md:px-gutter">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl text-on-surface font-semibold" style={ff}>
              Why use Learnex as your AI study tool?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map(feature => (
              <div
                key={feature.title}
                className="bg-surface-white rounded-3xl p-8 shadow-[0_4px_24px_rgba(36,31,26,0.04)] flex flex-col items-start text-left"
              >
                <div className={`w-12 h-12 ${feature.iconBg} ${feature.iconColor} rounded-[14px] flex items-center justify-center mb-6`}>
                  <span className="material-symbols-outlined" style={{ fontSize: '24px' }} aria-hidden="true">
                    {feature.icon}
                  </span>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-on-surface" style={ff}>{feature.title}</h3>
                <p className="text-on-surface-variant text-sm leading-relaxed">{feature.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section className="bg-paper-bg border-t border-outline-variant/10 pt-24">
        <PricingContent showCompare={false} isLandingPage={true} />
      </section>
      

      {/* ── Quick Answers (AEO) ── */}
      <section className="py-24 bg-[#FBF7F1] border-t border-outline-variant/10">
        <div className="max-w-[760px] mx-auto px-base md:px-gutter">
          {/* Header — mirrors FaqPage */}
          <p className="text-label-caps text-ink-muted mb-3" style={mono}>FAQ</p>
          <h2 className="text-4xl md:text-5xl font-bold text-on-surface mb-4" style={ff}>
            Quick answers
          </h2>
          <p className="text-body-lg text-on-surface-variant mb-12">
            Common questions about Learnex. Can't find yours?{' '}
            <a href="mailto:support@learnex.cc" className="underline text-primary-container">
              support@learnex.cc
            </a>
          </p>

          {/* Accordion card — same as FaqPage */}
          <div className="bg-surface-white border border-outline-variant/20 rounded-3xl px-6 shadow-sm">
            {landingFaq.map((item) => (
              <AccordionItem key={item.q} q={item.q} a={item.a} />
            ))}
          </div>
        </div>
      </section>
      <PublicFooter />

      {/* ── YouTube Video Modal (Portal → mounts on document.body) ── */}
      {videoOpen && createPortal(
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setVideoOpen(false)}
        >
          {/* Backdrop */}
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }} />

          {/* Card */}
          <div
            style={{ position: 'relative', zIndex: 10, width: '100%', maxWidth: 896, borderRadius: 16, boxShadow: '0 25px 60px rgba(0,0,0,0.5)', background: '#000' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setVideoOpen(false)}
              style={{ position: 'absolute', top: 12, right: 12, zIndex: 30, width: 36, height: 36, borderRadius: '50%', background: 'rgba(0,0,0,0.65)', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              aria-label="Close video"
            >
              <span className="material-symbols-outlined" style={{ fontSize: 20 }} aria-hidden="true">close</span>
            </button>

            {/* 16:9 container */}
            <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: 16 }}>
              <iframe
                src="https://www.youtube.com/embed/J565dCUb3aE?autoplay=1&rel=0&modestbranding=1"
                title="How Learnex works"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none', display: 'block' }}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

